import type { Address, Hex } from 'viem'

/**
 * The acknowledgement a wallet signs before its first trade from OUR trade panel of a competition: a FREE off-chain EIP-712 message (no gas). The
 * server (competitions_v3/ack.py) rebuilds exactly this message from its own database row and recovers the signer, so the client sends only the
 * wallet, the timestamp and the signature. It is not an eligibility condition: the on-chain rules count every qualifying trade whether or not anybody
 * signed anything, and the signature only unlocks this panel.
 */
export const ACK_STATEMENT = 'I understand only trades through this pool count'
export const ACK_DOMAIN_NAME = 'CADINUdex Competition Rules'

export const ACK_TYPES = {
  RulesAcknowledgement: [
    { name: 'competitionId', type: 'uint256' },
    { name: 'pool', type: 'address' },
    { name: 'startTime', type: 'uint256' },
    { name: 'endTime', type: 'uint256' },
    { name: 'rulesHash', type: 'bytes32' },
    { name: 'wallet', type: 'address' },
    { name: 'timestamp', type: 'uint256' },
    { name: 'statement', type: 'string' },
  ],
} as const

export interface AckCompetition {
  id: number
  pool: string
  startTime: number
  endTime: number
  rulesHash: string
}

export function ackTypedData(c: AckCompetition, chain: { chainId: number; contract: string }, wallet: string, timestamp: number) {
  return {
    domain: { name: ACK_DOMAIN_NAME, version: '1', chainId: chain.chainId, verifyingContract: chain.contract.toLowerCase() as Address },
    types: ACK_TYPES,
    primaryType: 'RulesAcknowledgement' as const,
    message: {
      competitionId: BigInt(c.id),
      pool: c.pool.toLowerCase() as Address,
      startTime: BigInt(c.startTime),
      endTime: BigInt(c.endTime),
      rulesHash: c.rulesHash.toLowerCase() as Hex,
      wallet: wallet.toLowerCase() as Address,
      timestamp: BigInt(timestamp),
      statement: ACK_STATEMENT,
    },
  }
}

export interface AckStatus {
  acknowledged: boolean
  at: string | null
  competitionId: number
}

export class AckError extends Error {
  readonly code: string

  constructor(code: string) {
    super(code)
    this.name = 'AckError'
    this.code = code
  }
}

const TEXT: Record<string, string> = {
  stale_timestamp: 'Your device clock is off by more than ten minutes. Fix the time and try again.',
  signer_mismatch: 'The signature did not match this wallet. Try again.',
  bad_signature: 'The signature could not be read. Try again.',
  rate_limited: 'Too many attempts. Wait a little and try again.',
  rules_unavailable: 'This competition\'s rules are not available yet.',
  'not found': 'This competition was not found.',
}
export const ackErrorText = (code: string) => TEXT[code] ?? 'The acknowledgement could not be saved. Try again in a moment.'

export async function getAck(api: string, id: number, wallet: string): Promise<AckStatus> {
  const r = await fetch(`${api}/${id}/ack?wallet=${wallet}`, { headers: { accept: 'application/json' } })
  if (!r.ok) throw new AckError(String(r.status))
  return r.json()
}

export async function postAck(api: string, id: number, body: { wallet: string; timestamp: number; signature: string }): Promise<AckStatus & { already?: boolean }> {
  const r = await fetch(`${api}/${id}/ack`, { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body) })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) throw new AckError(typeof j?.error === 'string' ? j.error : String(r.status))
  return j
}
